import React, { useState } from 'react';
import ProductViewer3D from "./ProductViewer3D";
import { motion, AnimatePresence } from 'framer-motion';
import { X, ShoppingCart, Tag, Package, Star, Download } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { supabase } from '../utils/supabaseClient';

//-- Proyecto diseñado, programado, testeado, ejecutado y publicado por Alex Castillo.
//-- Autor: Alex Castillo © 2026 Alex Castillo – Todos los derechos reservados.

const ProductModal = ({ product, isOpen, onClose }) => {
  const { addToCart } = useApp();
  
  const [showForm, setShowForm] = useState(false);
  const [customerData, setCustomerData] = useState({
    name: '',
    nit: '',
    company: '',
    email: '',
    phone: ''
  });

  const handleAddToCart = () => {
    addToCart(product);
    onClose();
  };

  const handleDownloadDatasheet = () => {
    if (!product.datasheet) {
      alert("Ficha técnica no disponible");
      return;
    }

    setShowForm(true); // 👈 abre el formulario
  };

  const handleSubmitForm = async (e) => {
    e.preventDefault();

    const { data, error } = await supabase
      .from('fichas_tecnicas')
      .insert([{
        name: customerData.name,
        nit: customerData.nit,
        company: customerData.company,
        email: customerData.email,
        phone: customerData.phone,
        product_reference: product.reference
      }])
      .select();

    console.log("DATA:", data);
    console.log("ERROR:", error);

    if (error) {
      console.error(error);
      alert("Error, comuniquese con el área de soporte marcando al +57 3183473820.");
      return;
    }

    // Descargar archivo
    window.open(product.datasheet, "_blank");

    setShowForm(false);
    setCustomerData({
      name: '',
      nit: '',
      company: '',
      email: '',
      phone: ''
    });
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
        >
          <motion.div
            className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden"
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-2xl font-bold bg-gradient-to-r from-blue-700 to-blue-600 bg-clip-text text-transparent">
                Detalles del Producto
              </h2>
              <button
                onClick={onClose}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="overflow-y-auto max-h-[calc(90vh-80px)]">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 p-6">
                <div className="space-y-4">
                  <div className="relative overflow-hidden rounded-xl group">
                    <img
                      src={product.image}
                      alt={product.name}
                      className="w-full h-80 object-contain p-4 transition-transform duration-700 ease-in-out group-hover:scale-110"
                    />
                    <div className="absolute top-4 right-4 bg-gradient-to-r from-blue-600 to-blue-700 text-white px-3 py-1 rounded-full text-sm font-semibold">
                      {product.category}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-gradient-to-r from-blue-50 to-white p-4 rounded-xl border border-blue-200">
                      <div className="flex items-center gap-2 mb-2">
                        <Package className="w-4 h-4 text-blue-600" />
                        <span className="text-sm font-semibold text-blue-700">Referencia</span>
                      </div>
                      <p className="text-blue-800 font-bold">{product.reference}</p>
                    </div>
                  </div>

                  {product.model3d && (
                    <div className="mt-4">
                      <ProductViewer3D model={product.model3d} />
                    </div>
                  )}

                </div>

                <div className="space-y-6">
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-3">{product.name}</h3>
                    <p className="text-gray-700 text-justify leading-relaxed">
                      {product.fullDescription || product.description}
                    </p>
                  </div>

                  <div className="bg-gradient-to-r from-gray-100 to-gray-400 p-6 rounded-xl">
                    <h4 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                      <Star className="w-5 h-5 text-yellow-500" />
                      Características Principales
                    </h4>
                    <ul className="space-y-1">
                      {product.features && product.features.map((feature, index) => (
                        <li key={index} className="flex-none items-center gap-3 text-justify text-gray-700">
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="border-t pt-6">
                    <div className="flex flex-col gap-4">
                      <div className="flex flex-col sm:flex-row gap-4">
                        <motion.button
                          onClick={handleDownloadDatasheet}
                          className="flex-1 bg-gradient-to-r from-red-500 to-red-800 text-white py-4 px-6 rounded-xl font-semibold text-lg flex items-center justify-center gap-3 shadow-lg shadow-gray-400 hover:shadow-xl hover:shadow-gray-700 transition-all"
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                        >
                          <Download className="w-6 h-6" />
                          Descargar Ficha Técnica
                        </motion.button>

                        <motion.button
                          onClick={handleAddToCart}
                          className="flex-1 bg-gradient-to-r from-blue-500 to-blue-800 text-white py-4 px-6 rounded-xl font-semibold text-lg flex items-center justify-center gap-3 shadow-lg shadow-gray-400 hover:shadow-xl hover:shadow-gray-700 transition-all"
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                        >
                          <ShoppingCart className="w-6 h-6" />
                          Agregar al Carrito
                        </motion.button>
                      </div>

                      <motion.button
                        onClick={() => window.open('https://wa.me/573174295659?text=Cordial Saludo, tengo interes en el producto: ' + product.name, '_blank')}
                        className="w-full bg-gradient-to-r from-green-500 to-green-800 text-white py-4 px-6 rounded-xl font-semibold text-lg justify-center gap-3 shadow-lg shadow-gray-400 hover:shadow-xl hover:shadow-gray-700 transition-all"
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        💬 Consultar por WhatsApp
                      </motion.button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}

      {showForm && (
        <motion.div
          className="fixed inset-0 flex items-center justify-center bg-black/40 z-[50] p-4 overflow-y-auto"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <motion.div
            className="bg-white rounded-2xl p-6 w-full max-w-md shadow-xl"
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
          >
            <h3 className="text-xl font-bold mb-6 text-center">
              Datos para descargar ficha técnica
            </h3>

            <form onSubmit={handleSubmitForm} className="space-y-4">

              <input
                type="text"
                placeholder="Nombre completo"
                value={customerData.name}
                onChange={(e) => setCustomerData({ ...customerData, name: e.target.value })}
                required
                className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500"
              />

              <input
                type="text"
                placeholder="NIT - CC"
                value={customerData.nit}
                onChange={(e) => setCustomerData({ ...customerData, nit: e.target.value })}
                required
                className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500"
              />

              <input
                type="text"
                placeholder="Empresa"
                value={customerData.company}
                onChange={(e) => setCustomerData({ ...customerData, company: e.target.value })}
                required
                className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500"
              />

              <input
                type="email"
                placeholder="Correo electrónico"
                value={customerData.email}
                onChange={(e) => setCustomerData({ ...customerData, email: e.target.value })}
                required
                className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500"
              />

              <input
                type="tel"
                placeholder="Número de teléfono"
                value={customerData.phone}
                onChange={(e) => setCustomerData({ ...customerData, phone: e.target.value })}
                required
                className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500"
              />

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className="flex-1 bg-gray-500 text-white py-3 rounded-lg font-semibold"
                >
                  Cancelar
                </button>

                <button
                  type="submit"
                  className="flex-1 bg-gradient-to-r from-red-500 to-red-800 text-white py-3 rounded-lg font-semibold"
                >
                  Ir a ficha técnica
                </button>
              </div>

            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ProductModal;

//-- Proyecto diseñado, programado, testeado, ejecutado y publicado por Alex Castillo.
//-- Autor: Alex Castillo © 2026 Alex Castillo – Todos los derechos reservados.